import React, { useRef, useState, useEffect } from 'react';
import { useStoreActions } from 'easy-peasy';
import { useReactToPrint } from 'react-to-print';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

import logo from "../../../assets/images/svg/logo-flex-sacarabany-sarl.svg";

import { getQuantityDisplay } from '../../tools/manager';

// import { decodeAndNormalizeSignatureSvg } from "./tools/signature";

import "./css/pdf.css";
import base64 from 'react-native-base64';

const PdfPurchaseOrders = () => {
  const { idpurchaseorder } = useParams();

  // 🌱 store
  const getOrderCoupon = useStoreActions(
    (actions) => actions.middleware.OrdersMiddleware.getOrderCoupon
  );

  // états
  const [dataPdf, setDataPdf] = useState(null);
  const [materialsInd, setMaterialsInd] = useState([]);
  const [materialsComp, setMaterialsComp] = useState([]);
  const [mySvg, setMySvg] = useState('');

  const navigate = useNavigate();

  const componentRef = useRef();
  const handlePdf = useReactToPrint({
    contentRef: componentRef,
    content: () => componentRef.current,
    documentTitle: 'purchase-data',
    onAfterPrint: () => toast.success('✅ Impression réussie'),
  });

  useEffect(() => {
    callOrderCoupon();
  }, []);

  const callOrderCoupon = async () => {
    try {
      const res = await getOrderCoupon(idpurchaseorder);
      // setMySvg(res?.signed ? decodeAndNormalizeSignatureSvg(res.signed) : '');
      setMySvg(res?.signed ? base64.decode(res.signed) : '');
      setDataPdf(res);

      const materialData = res.materialData.items || [];
      setMaterialsInd(materialData.filter((m) => m.indexRef != null));
      setMaterialsComp(materialData.filter((m) => m.indexRef == null));
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ Erreur lors du chargement du bon de commande');
    }
  };

  const formattedDate = (updatedAt) => {
    const dateA = updatedAt ? parseISO(updatedAt) : new Date();
    return format(dateA, "EEEE dd MMMM yyyy 'à' HH:mm", { locale: fr });
  };

  return (
    <section id="pagetodonwload" className="ml-8 mt-14 mb-10 flex space-x-7">
      {/* Zone imprimable */}
      <div ref={componentRef} className="w-[800px] h-fit rounded-lg bg-white shadow-md">
        {/* En-tête */}
        <div className="flex justify-between items-center border-b border-gray-300 p-6">
          <img src={logo} alt="Logo entreprise" className="h-16 object-contain" />
          <div className="text-right text-sm">
            <p className="font-semibold text-lg">BON DE COMMANDE</p>
            <p>N° {dataPdf?.numero || '-'}</p>
            <p className="text-gray-500">Le {formattedDate(dataPdf?.createdAt)}</p>
          </div>
        </div>

        {/* Infos principales */}
        <div className="mt-10 px-10">
          <div className="flex justify-between">
            {/* Société */}
            <div className="mr-20 flex flex-col space-y-1 text-sm">
              <h4 className="font-semibold">SACARABANY SARL</h4>
              <p>279 rue solitude la mulatresse</p>
              <p>97200 Fort de France</p>
              <p>SIRET : 495 262 131 00016</p>
              <p>contact@sacarabany-sarl.fr</p>
              {dataPdf?.collaborator && (
                <p>
                  Fait par:{' '}
                  <span className="font-medium">
                    {dataPdf.collaborator.first_name} {dataPdf.collaborator.last_name}
                  </span>
                </p>
              )}
            </div>

            {/* Client */}
            <div className="flex flex-col space-y-1 text-sm">
              <h4 className="font-semibold">{dataPdf?.company?.name?.toUpperCase()}</h4>
              <p>{dataPdf?.company?.address}</p>
              <p>SIRET : {dataPdf?.company?.siret}</p>
              <p>{dataPdf?.company?.mail?.split(',')[0]}</p>
              {dataPdf?.nameClient && (
                <p>
                  Client : <span className="font-medium">{dataPdf?.nameClient}</span>
                </p>
              )}
            </div>
          </div>

          {/* Chantier */}
          <div className="mt-10">
            <h4 className="font-semibold text-base">
              {dataPdf?.worksite?.name?.toUpperCase() ||
                dataPdf?.nameWorksite?.toUpperCase()}
            </h4>
          </div>

          {/* Table Matériels indispensables */}
          <div className="mt-10">
            <h4 className="font-semibold mb-2">Matériel indispensable</h4>
            <table className="w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Dénomination</th>
                  <th className="p-2 border">Référence</th>
                  <th className="p-2 border">Quantité</th>
                </tr>
              </thead>
              <tbody>
                {materialsInd.length ? (
                  materialsInd.map((mat, idx) => {
                    const refs =
                      mat?.materialSettings?.references?.[0]?.reference?.split(',') ||
                      [];
                    const myRef = refs[mat.indexRef] || '-';
                    return (
                      <tr key={idx}>
                        <td className="p-2 border">{mat?.materialSettings?.name}</td>
                        <td className="p-2 border">{myRef}</td>
                        <td className="p-2 border">{getQuantityDisplay(mat)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="p-2 border text-center" colSpan={3}>
                      Aucun matériel indispensable
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Table Matériels complémentaires */}
          <div className="mt-10">
            <h4 className="font-semibold mb-2">Matériel complémentaire</h4>
            <table className="w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Dénomination</th>
                  <th className="p-2 border">Référence</th>
                  <th className="p-2 border">Quantité</th>
                </tr>
              </thead>
              <tbody>
                {materialsComp.length ? (
                  materialsComp.map((mat, idx) => (
                    console.log('mat', mat),
                    <tr key={idx}>
                      <td className="p-2 border">{mat?.materialSettings?.name || mat?.name}</td>
                      <td className="p-2 border">{mat?.description}</td>
                      <td className="p-2 border">{getQuantityDisplay(mat)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-2 border text-center" colSpan={3}>
                      Aucun matériel complémentaire
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Signature */}
          <div className="text-right flex flex-col items-end justify-end">
            <p className="font-medium px-11">Signature :</p>
            {!!mySvg && (
              <div
                // className="signature-wrapper"
                className="bg-cover"
                dangerouslySetInnerHTML={{ __html: mySvg }}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-xs border-t border-gray-300 pt-4 pb-6 mt-10">
          <h6>SACARABANY SARL - SIRET : 495 262 131 00016 - APE : 453 A</h6>
          <p>279 rue solitude la mulatresse - 97200 Fort de France</p>
          <p>TEL : 0596 70 75 50 - 0696 45 02 08</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col justify-between text-blue-admin">
        <button
          onClick={() => navigate(-1)}
          className="border border-blue-admin py-3 px-4 rounded-xl"
        >
          Retour aux bons de commande
        </button>
        <button
          className="bg-blue-admin text-white py-4 rounded-xl mt-4"
          onClick={handlePdf}
        >
          Générer le PDF
        </button>
      </div>
    </section>
  );
};

export default PdfPurchaseOrders;
